import {
  ChevronRightIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import {
  AssetData,
  CTA,
  SectionData,
  SectionTypeStructureField,
  SectionTypeStructureFieldBoolean,
  SectionTypeStructureFieldCta,
  SectionTypeStructureFieldCtaList,
  SectionTypeStructureFieldCustomCode,
  SectionTypeStructureFieldForm,
  SectionTypeStructureFieldImage,
  SectionTypeStructureFieldList,
  SectionTypeStructureFieldLiteral,
  SectionTypeStructureFieldNumber,
  SectionTypeStructureFieldObject,
  SectionTypeStructureFieldString,
  SectionTypeStructureFieldText,
  TextOrQuillText,
  WithId,
} from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import CodeInput from '@superfunnel/ui-core/v2/CodeInput';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@superfunnel/ui-core/v2/Collapsible';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import Label from '@superfunnel/ui-core/v2/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import Switch from '@superfunnel/ui-core/v2/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import {
  formFieldEmailValidator,
  formFieldUrlWithParamsValidator,
} from '@superfunnel/utils';
import { useContext, useMemo } from 'react';
import { uid } from 'uid';
import { AISuggestionsContext } from '../../contexts/AISuggestionsContextProvider';
import { camelCaseToTitle, capitalize, classNames } from '../../utils';
import { extractTextFromQuillTrimmed } from '../../utils/quill';
import CTAListEditor from '../CTAListEditor';
import FileUpload from '../FormFields/FileUpload/FileUpload';
import { FormOrQuizSelector } from '../FormFields/FormOrQuizSelector';
import QuillField from '../QuillField';
import SectionItemsListEditor from '../SectionItemsListEditor';

type Content = SectionData | WithId<Record<string, any>>;

interface Props<T extends Content> {
  content: T;
  fields: SectionTypeStructureField[];
  onChange: (content: T) => void;
  isSection?: boolean;
  className?: string;
}

export function SectionStructureForm<T extends Content>({
  content,
  fields,
  onChange,
  isSection,
  className,
}: Props<T>) {
  return (
    <div key={content.id} className={classNames('space-y-2', className)}>
      {fields?.map((field) => (
        <SectionStructureFormField
          key={field.name}
          content={content}
          field={field}
          onChange={onChange}
          isSection={isSection}
        />
      ))}
    </div>
  );
}

interface SectionStructureFormFieldProps<
  T extends Content,
  F extends SectionTypeStructureField = SectionTypeStructureField,
> {
  content: T;
  field: F;
  onChange: (content: T) => void;
  isSection?: boolean;
}

function SectionStructureFormField<T extends Content>({
  field,
  ...props
}: SectionStructureFormFieldProps<T>) {
  switch (field.type) {
    case 'text':
      return <SectionStructureFormFieldText field={field} {...props} />;

    case 'string':
      return <SectionStructureFormFieldString field={field} {...props} />;

    case 'number':
      return <SectionStructureFormFieldNumber field={field} {...props} />;

    case 'boolean':
      return <SectionStructureFormFieldBoolean field={field} {...props} />;

    case 'literal':
      return <SectionStructureFormFieldLiteral field={field} {...props} />;

    case 'ctaList':
      return <SectionStructureFormFieldCTAList field={field} {...props} />;

    case 'cta':
      return <SectionStructureFormFieldCTA field={field} {...props} />;

    case 'image':
      return <SectionStructureFormFieldImage field={field} {...props} />;

    case 'custom-code':
      return <SectionStructureFormFieldCustomCode field={field} {...props} />;

    case 'form':
      return <SectionStructureFormFieldForm field={field} {...props} />;

    case 'list':
      return <SectionStructureFormFieldList field={field} {...props} />;

    case 'object':
      return <SectionStructureFormFieldObject field={field} {...props} />;
  }

  Sentry.captureException(
    new Error(
      `Unsupported field type in SectionStructureForm: ${field.type} in ${field.name} in ${props.content.type}`,
    ),
  );
  console.error(
    `Unsupported field type in SectionStructureForm: ${field.type} in ${field.name} in ${props.content.type}`,
  );
  return <div></div>;
}

function SectionStructureFormFieldText<T extends Content>({
  content,
  field,
  onChange,
  isSection,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldText>) {
  const { fieldSuggestions } = useContext(AISuggestionsContext);

  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as TextOrQuillText | undefined;
  const hideFieldName = `hide${capitalize(field.name)}` as keyof T;

  const isHidable =
    isSection && ['title', 'subtitle', 'tagline'].includes(field.name);

  return (
    <QuillField
      key={fieldName as string}
      title={fieldTitle}
      value={fieldValue}
      onChange={(value) => {
        onChange({
          ...content,
          [fieldName]: extractTextFromQuillTrimmed(value) ? value : null,
        });
      }}
      debounce={isSection}
      readOnly={isHidable ? !!content[hideFieldName] : false}
      trailing={
        isHidable ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onChange({
                      ...content,
                      [hideFieldName]: !content[hideFieldName],
                    });
                  }}
                >
                  {content[hideFieldName] ? (
                    <EyeSlashIcon className="h-3 w-3" />
                  ) : (
                    <EyeIcon className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{content[hideFieldName] ? 'Show' : 'Hide'}</p>
            </TooltipContent>
          </Tooltip>
        ) : undefined
      }
      className={
        isHidable && content[hideFieldName] ? 'bg-gray-200 opacity-50' : ''
      }
      generateSuggestions={async (textSelection, fullLine, prompt) => {
        return fieldSuggestions(
          '',
          '',
          fieldTitle,
          extractTextFromQuillTrimmed(fullLine),
          extractTextFromQuillTrimmed(textSelection),
          prompt,
        );
      }}
    />
  );
}

function SectionStructureFormFieldString<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldString>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as string | undefined;

  const validation = useMemo(() => {
    const dataType = field.dataType;

    switch (dataType) {
      case 'email':
        return formFieldEmailValidator(fieldValue ?? '', false);

      case 'url':
        return formFieldUrlWithParamsValidator(fieldValue ?? '', false);
    }

    return undefined;
  }, [fieldValue, field.dataType]);

  return (
    <Input
      key={fieldName as string}
      title={fieldTitle}
      value={fieldValue}
      onChange={(value) => {
        onChange({ ...content, [fieldName]: value?.target.value });
      }}
      validation={validation}
    />
  );
}

function SectionStructureFormFieldNumber<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldNumber>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as string | undefined;

  return (
    <Input
      key={fieldName as string}
      title={fieldTitle}
      value={fieldValue?.toString() || ''}
      type="number"
      // input={{ max: 5, min: 0, step: 0.5 }}
      onChange={(e) => {
        onChange({ ...content, [fieldName]: +(e.target.value as any) });
      }}
    />
  );
}

function SectionStructureFormFieldBoolean<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldBoolean>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as boolean | undefined;

  return (
    <div key={fieldName as string} className="flex items-center space-x-3">
      <Switch
        checked={fieldValue ?? false}
        onCheckedChange={(checked) => {
          onChange({ ...content, [fieldName]: checked });
        }}
      />
      <Label className="text-sm font-medium">{fieldTitle}</Label>
    </div>
  );
}

function SectionStructureFormFieldLiteral<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldLiteral>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as string | undefined;

  return (
    <div className="flex flex-col justify-start gap-1">
      <FieldTitle title={fieldTitle} />
      <Select
        value={fieldValue}
        onValueChange={(value: string) => {
          onChange({ ...content, [fieldName]: value });
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {field.options.map((option) => {
            const value = typeof option === 'string' ? option : option.value;
            const label = typeof option === 'string' ? option : option.label;
            return (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

function SectionStructureFormFieldCTAList<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldCtaList>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || 'Buttons';
  const fieldValue = content[fieldName] as CTA[] | undefined;

  return (
    <CTAListEditor
      title={fieldTitle}
      cta={fieldValue}
      onChange={(cta) => {
        onChange({ ...content, [fieldName]: cta });
      }}
    />
  );
}

function SectionStructureFormFieldCTA<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldCta>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || 'Buttons';
  const fieldValue = content[fieldName] as CTA | undefined;

  return (
    <CTAListEditor
      disableEditAction={field.disableEditAction}
      title={fieldTitle}
      cta={fieldValue ? [fieldValue] : []}
      onChange={(cta) => {
        onChange({ ...content, [fieldName]: cta[0] || null });
      }}
    />
  );
}

function SectionStructureFormFieldImage<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldImage>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as AssetData | undefined;

  return (
    <div>
      <FieldTitle className="mb-2" title={fieldTitle} />
      <FileUpload
        size="medium"
        image={fieldValue}
        onSelected={(image) => {
          onChange({ ...content, [fieldName]: (image || null) as any });
        }}
        showImageSettings={field.imageType !== 'logo'}
        types={field.imageType === 'logo' ? ['image'] : ['image', 'video']}
      />
    </div>
  );
}

function SectionStructureFormFieldCustomCode<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldCustomCode>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as string | undefined;

  return (
    <CodeInput
      title={fieldTitle}
      titleClassName={
        'text-base-700 flex items-center gap-2 text-sm font-medium'
      }
      value={fieldValue ?? ''}
      onChange={(code) => {
        onChange({ ...content, [fieldName]: code });
      }}
      readOnly
      language={'html'}
      inlineLines={5}
      placeholder={'<!-- Add your custom code here -->'}
    />
  );
}

// Only supports one form or quiz per content
function SectionStructureFormFieldForm<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldForm>) {
  const fieldTitle = field.label || 'Form';

  return (
    <FormOrQuizSelector
      title={fieldTitle}
      formId={(content as any).quizId || (content as any).formId}
      onChange={(selection) => {
        if (!selection) {
          onChange({ ...content, formId: null, quizId: null } as T);
        } else if (selection.type === 'form') {
          onChange({ ...content, formId: selection.id, quizId: null } as T);
        } else {
          onChange({ ...content, formId: null, quizId: selection.id } as T);
        }
      }}
    />
  );
}

function SectionStructureFormFieldList<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldList>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = (content[fieldName] ?? []) as WithId<
    Record<string, any>
  >[];

  return (
    <SectionItemsListEditor
      title={fieldTitle}
      items={fieldValue}
      sectionType={content.type}
      field={field}
      onChange={(items) => {
        onChange({ ...content, [fieldName]: items as any });
      }}
    />
  );
}

function SectionStructureFormFieldObject<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldObject>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as
    | WithId<Record<string, any>>
    | undefined;

  return (
    <Collapsible className="group">
      <CollapsibleTrigger className="flex w-full items-center gap-1 py-2">
        <span className="block text-sm leading-6 font-medium text-gray-900">
          {fieldTitle}
        </span>
        <div className="flex-1" />
        <ChevronRightIcon className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pb-3">
          <SectionStructureForm
            content={fieldValue ?? { id: uid() }}
            fields={field.structure.fields}
            onChange={(value) => {
              onChange({ ...content, [fieldName]: value as any });
            }}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
